<template>
  <div>
    <!-- Banners -->
    <div
      v-for="banner in visibleBanners"
      :key="'banner-' + banner.id"
      class="intercom-banner"
      :style="{ backgroundColor: severityColors[banner.severity] || severityColors.info }"
    >
      <div
        class="flex align-items-center justify-content-between px-4 py-2"
        style="max-width: 1400px; margin: 0 auto;"
      >
        <div class="flex align-items-center gap-2">
          <i :class="severityIcons[banner.severity]" style="color: #fff; font-size: 1.1rem"></i>
          <span class="font-bold" style="color: #fff">{{ banner.title }}</span>
          <span
            v-if="banner.body"
            class="text-sm ml-1"
            style="color: rgba(255,255,255,0.9)"
          >— {{ banner.body }}</span>
        </div>
        <button
          v-if="!banner.non_dismissible"
          class="intercom-banner-close"
          @click="dismissBanner(banner)"
          aria-label="Dismiss"
        >
          <i class="pi pi-times"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import * as intercomApi from '@/api/intercom';
import { useAuthStore } from '@/stores';

const SEVERITY_COLORS = {
  info: '#3B82F6',
  warning: '#F59E0B',
  error: '#EF4444',
  success: '#22C55E',
};

const SEVERITY_ICONS = {
  info: 'pi pi-info-circle',
  warning: 'pi pi-exclamation-triangle',
  error: 'pi pi-times-circle',
  success: 'pi pi-check-circle',
};

const TOAST_SEVERITY_MAP = {
  info: 'info',
  warning: 'warn',
  error: 'error',
  success: 'success',
};

const POLL_INTERVAL = 30000;
const TOAST_LIFE = 10000;
const FLASH_INTERVAL = 1000;

export default {
  data() {
    return {
      messages: [],
      dismissedIds: new Set(),
      shownToastIds: new Set(),
      pollTimer: null,
      flashTimer: null,
      originalTitle: document.title,
      severityColors: SEVERITY_COLORS,
      severityIcons: SEVERITY_ICONS,
    };
  },
  computed: {
    authStore() {
      return useAuthStore();
    },
    currentRoute() {
      return this.$route?.path || '/';
    },
    filteredMessages() {
      return this.messages.filter((msg) => {
        if (this.dismissedIds.has(msg.id)) return false;
        if (msg.page_route && msg.page_route !== this.currentRoute) return false;
        return true;
      });
    },
    visibleBanners() {
      return this.filteredMessages.filter((msg) => msg.type === 'banner');
    },
  },
  watch: {
    'authStore.isAuthenticated': {
      handler(val) {
        if (val) {
          this.startPolling();
        } else {
          this.stopPolling();
          this.messages = [];
        }
      },
    },
    '$route.path': {
      handler() {
        this.processMessages();
      },
    },
  },
  mounted() {
    if (this.authStore.isAuthenticated) {
      this.startPolling();
    }
  },
  beforeUnmount() {
    this.stopPolling();
    this.stopFlashing();
  },
  methods: {
    startPolling() {
      this.fetchMessages();
      this.pollTimer = setInterval(() => {
        this.fetchMessages();
      }, POLL_INTERVAL);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async fetchMessages() {
      try {
        const res = await intercomApi.getUserMessages(this.currentRoute);
        if (res.success) {
          this.messages = res.data;
          this.processMessages();
        }
      } catch {
        // Silently fail polling
      }
    },
    processMessages() {
      this.filteredMessages.forEach((msg) => {
        if (msg.type === 'toast' && !this.shownToastIds.has(msg.id)) {
          this.showToast(msg);
          this.shownToastIds.add(msg.id);
        }

        if (msg.play_sound) {
          this.playSound();
        }

        if (msg.flash_tab) {
          this.startFlashing();
        }
      });
    },
    showToast(msg) {
      const severity = TOAST_SEVERITY_MAP[msg.severity] || 'info';
      this.$toast.add({
        severity,
        summary: msg.title,
        detail: msg.body,
        sticky: !!msg.non_dismissible,
        life: msg.non_dismissible ? undefined : TOAST_LIFE,
        closable: !msg.non_dismissible,
        onClose: () => {
          this.onDismiss(msg);
        },
      });
    },
    async onDismiss(msg) {
      this.dismissedIds.add(msg.id);
      try {
        await intercomApi.dismissMessage(msg.id);
      } catch {
        // Best-effort dismiss
      }
    },
    async dismissBanner(banner) {
      this.dismissedIds.add(banner.id);
      try {
        await intercomApi.dismissMessage(banner.id);
      } catch {
        // Best-effort dismiss
      }
    },
    playSound() {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        oscillator.frequency.value = 440;
        oscillator.type = 'sine';
        gain.gain.value = 0.3;
        oscillator.start();
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
        oscillator.stop(ctx.currentTime + 0.5);
      } catch {
        // Audio not available
      }
    },
    startFlashing() {
      if (this.flashTimer) return;
      let isFlashed = false;
      this.originalTitle = document.title;

      const onFocus = () => {
        this.stopFlashing();
        window.removeEventListener('focus', onFocus);
      };
      window.addEventListener('focus', onFocus);

      this.flashTimer = setInterval(() => {
        isFlashed = !isFlashed;
        document.title = isFlashed ? '🔔 New Message!' : this.originalTitle;
      }, FLASH_INTERVAL);
    },
    stopFlashing() {
      if (this.flashTimer) {
        clearInterval(this.flashTimer);
        this.flashTimer = null;
        document.title = this.originalTitle;
      }
    },
  },
};
</script>

<style scoped>
.intercom-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

.intercom-banner-close {
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.intercom-banner-close:hover {
  background-color: rgba(255, 255, 255, 0.2);
}
</style>
